import { ChartTheme } from './types';
import { Theme } from '@/shared/constants/themes';

/**
 * Récupère les couleurs du thème actuel
 * Soit depuis un objet Theme fourni (réactif), soit depuis le DOM (fallback)
 */
export function getChartTheme(appTheme?: Theme): ChartTheme {
    if (appTheme) {
        const colors = appTheme.colors;
        return {
            backgroundColor: colors.background,
            textColor: colors.foreground,
            gridColor: colors.border,
            primaryColor: colors.primary,
            secondaryColor: colors.secondary,
            successColor: colors.success,
            warningColor: colors.warning,
            errorColor: colors.error,
            colors: [
                colors.primary,
                colors.secondary,
                colors.accent || colors.primary,
                colors.success,
                colors.warning,
                '#8b5cf6', // violet
                '#06b6d4', // cyan
                '#f97316', // orange
            ],
        };
    }

    const root = document.documentElement;
    const style = getComputedStyle(root);

    return {
        backgroundColor: style.getPropertyValue('--background').trim() || '#ffffff',
        textColor: style.getPropertyValue('--foreground').trim() || '#111827',
        gridColor: style.getPropertyValue('--border').trim() || '#e5e7eb',
        primaryColor: style.getPropertyValue('--primary').trim() || '#6d28d9',
        secondaryColor: style.getPropertyValue('--secondary').trim() || '#ec4899',
        successColor: style.getPropertyValue('--success').trim() || '#10b981',
        warningColor: style.getPropertyValue('--warning').trim() || '#f59e0b',
        errorColor: style.getPropertyValue('--error').trim() || '#ef4444',
        colors: [
            style.getPropertyValue('--primary').trim() || '#6d28d9',
            style.getPropertyValue('--secondary').trim() || '#ec4899',
            style.getPropertyValue('--accent').trim() || '#14b8a6',
            style.getPropertyValue('--success').trim() || '#10b981',
            style.getPropertyValue('--warning').trim() || '#f59e0b',
            '#8b5cf6', // violet
            '#06b6d4', // cyan
            '#f97316', // orange
        ],
    };
}

/**
 * Options Highcharts de base avec le thème
 */
export function getBaseChartOptions(theme: ChartTheme): Highcharts.Options {
    return {
        chart: {
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, sans-serif',
            },
        },
        title: {
            style: {
                color: theme.textColor,
                fontSize: '16px',
                fontWeight: '600',
            },
        },
        subtitle: {
            style: {
                color: theme.textColor,
                opacity: 0.7,
            },
        },
        xAxis: {
            labels: {
                style: { color: theme.textColor, fontSize: '11px' },
            },
            lineColor: theme.gridColor,
            tickColor: theme.gridColor,
            gridLineColor: theme.gridColor,
        },
        yAxis: {
            labels: {
                style: { color: theme.textColor, fontSize: '11px' },
            },
            gridLineColor: theme.gridColor,
            title: {
                style: { color: theme.textColor },
            },
        },
        legend: {
            itemStyle: {
                color: theme.textColor,
                fontWeight: '500',
            },
            itemHoverStyle: {
                color: theme.primaryColor,
            },
        },
        tooltip: {
            backgroundColor: theme.backgroundColor,
            borderColor: theme.gridColor,
            style: {
                color: theme.textColor,
            },
            shadow: true,
        },
        plotOptions: {
            series: {
                animation: {
                    duration: 800,
                },
            },
        },
        colors: theme.colors,
        credits: {
            enabled: false,
        },
    };
}

/**
 * Formatte un montant en euros
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Formatte un pourcentage
 */
export function formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
}

/**
 * Formatte une durée en minutes/heures
 */
export function formatDuration(minutes: number): string {
    if (minutes < 60) {
        return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
}

/**
 * Formatte une date YYYY-MM-DD pour l'affichage (évite décalage UTC).
 */
export function formatDate(dateStr: string): string {
    const date = new Date(dateStr + 'T12:00:00');
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
    }).format(date);
}
